import { ComponentHealthCondition } from 'features/fde/assetHealthPlanning/componentHealthConditions'
import {
  generateSiteViewCheckerboardColor,
  generateSiteViewStripedColor,
  useSiteViewColors,
} from 'features/overview/utils/siteViewTheme'
import isNil from 'lodash/isNil'
import React from 'react'
import { ThemeType } from 'styles/GeckoTheme'
import { NoRLHealthScore } from 'types'
import { updateColorOpacity } from 'utils/colorHelpers'

import { AssetTrackerBreakdown } from './assembleAssetTrackerBreakdown'
import { SiteViewAssetTrackerCountDisplay } from './SiteViewAssetTrackerCountDisplay'

/**
 * Gets the count-display objects for the health score breakdown
 */
export function getHealthScoreCountDisplayObjs(
  breakdown: AssetTrackerBreakdown,
  siteViewColors: ReturnType<typeof useSiteViewColors>,
  theme: ThemeType,
  selectedBreakdown:
    | ComponentHealthCondition
    | NoRLHealthScore.NotEnoughData
    | undefined
) {
  const goodCount =
    breakdown[ComponentHealthCondition.Excellent].aiData +
    breakdown[ComponentHealthCondition.Excellent].traditionalData

  const fairCount =
    breakdown[ComponentHealthCondition.Fair].aiData +
    breakdown[ComponentHealthCondition.Fair].traditionalData

  const badCount =
    breakdown[ComponentHealthCondition.Bad].aiData +
    breakdown[ComponentHealthCondition.Bad].traditionalData

  const criticalCount =
    breakdown[ComponentHealthCondition.VeryPoor].aiData +
    breakdown[ComponentHealthCondition.VeryPoor].traditionalData

  const notEnoughDataCount = breakdown[NoRLHealthScore.NotEnoughData]

  return [
    <SiteViewAssetTrackerCountDisplay
      key={'health-score-cd-good'}
      label={ComponentHealthCondition.Excellent}
      count={goodCount}
      backgroundColor={siteViewColors[ComponentHealthCondition.Excellent]}
      focused={
        isNil(selectedBreakdown)
          ? true
          : selectedBreakdown === ComponentHealthCondition.Excellent
      }
    />,
    <SiteViewAssetTrackerCountDisplay
      key={'health-score-cd-fair'}
      label={ComponentHealthCondition.Fair}
      count={fairCount}
      backgroundColor={siteViewColors[ComponentHealthCondition.Fair]}
      focused={
        isNil(selectedBreakdown)
          ? true
          : selectedBreakdown === ComponentHealthCondition.Fair
      }
    />,
    <SiteViewAssetTrackerCountDisplay
      key={'health-score-cd-bad'}
      label={ComponentHealthCondition.Bad}
      count={badCount}
      backgroundColor={siteViewColors[ComponentHealthCondition.Bad]}
      focused={
        isNil(selectedBreakdown)
          ? true
          : selectedBreakdown === ComponentHealthCondition.Bad
      }
    />,
    <SiteViewAssetTrackerCountDisplay
      key={'health-score-cd-critical'}
      label={ComponentHealthCondition.VeryPoor}
      count={criticalCount}
      backgroundColor={siteViewColors[ComponentHealthCondition.VeryPoor]}
      focused={
        isNil(selectedBreakdown)
          ? true
          : selectedBreakdown === ComponentHealthCondition.VeryPoor
      }
    />,
    <SiteViewAssetTrackerCountDisplay
      key={'data-quality-cd-not-enough-data'}
      label={'Not enough data'}
      count={notEnoughDataCount}
      backgroundColor={generateSiteViewCheckerboardColor(
        siteViewColors[NoRLHealthScore.NotEnoughData],
        theme.forecast.background1
      )}
      borderColor={siteViewColors[NoRLHealthScore.NotEnoughData]}
      focused={
        isNil(selectedBreakdown)
          ? true
          : selectedBreakdown === NoRLHealthScore.NotEnoughData
      }
    />,
  ]
}

/**
 * Gets the count-display objects for the data quality breakdown
 */
export function getDataQualityCountDisplayObjs(
  breakdown: AssetTrackerBreakdown,
  colors: ReturnType<typeof useSiteViewColors>,
  theme: ThemeType,
  selectedBreakdown:
    | ComponentHealthCondition
    | NoRLHealthScore.NotEnoughData
    | undefined
) {
  const geckoDataCount = Object.entries(breakdown).reduce<number>(
    (sum, [key, data]) => {
      if (!isNil(selectedBreakdown) && key !== selectedBreakdown) {
        return sum
      }

      if (typeof data === 'number') {
        // This means the data is of either the 'no inspection' or 'not enough data' type
        return sum
      }
      return sum + data.aiData
    },
    0
  )

  const sparseDataCount = Object.entries(breakdown).reduce<number>(
    (sum, [key, data]) => {
      if (!isNil(selectedBreakdown) && key !== selectedBreakdown) {
        return sum
      }

      if (typeof data === 'number') {
        // This means the data is of either the 'no inspection' or 'not enough data' type
        return sum
      }
      return sum + data.traditionalData
    },
    0
  )

  const noInspectionCount = breakdown[NoRLHealthScore.NoInspection]

  const basicColor = updateColorOpacity(
    colors[NoRLHealthScore.NotEnoughData],
    1
  )

  return [
    <SiteViewAssetTrackerCountDisplay
      key={'data-quality-cd-gecko-data'}
      label={'AI + Robotics'}
      count={geckoDataCount}
      backgroundColor={basicColor}
    />,
    <SiteViewAssetTrackerCountDisplay
      key={'data-quality-cd-sparse-data'}
      label={'Traditional'}
      count={sparseDataCount}
      backgroundColor={generateSiteViewStripedColor(
        basicColor,
        theme.forecast.background1
      )}
      borderColor={basicColor}
    />,

    <SiteViewAssetTrackerCountDisplay
      key={'data-quality-cd-no-inspection-data'}
      label={'No inspection data'}
      count={noInspectionCount}
      backgroundColor={colors[NoRLHealthScore.NoInspection]}
      focused={isNil(selectedBreakdown)}
    />,
  ]
}
